/**
 * avatar-animator.js
 * Bộ điều khiển cử động (Animation Controller) cho nhân vật hình hộp
 * Xử lý nội suy mượt mà (lerp) giữa các tư thế và cử động tự nhiên.
 * Hỗ trợ cả hoạt hình tuần hoàn (Loop) và hoạt hình một lần (One-shot)
 */

import { easeInOutQuad, lerp, lerpPose } from '../core/easing.js';
import { ACTION_POSES } from './poses/index.js';

export class AvatarAnimator {
  constructor(avatarMeshMap) {
    this.parts = avatarMeshMap; // { root, pelvis, torso, head, leftArm, rightArm, leftLeg, rightLeg, basePelvisY }
    this.basePelvisY = avatarMeshMap.basePelvisY !== undefined ? avatarMeshMap.basePelvisY : 0.65;
    this.currentAction = 'idle';
    this.targetAction = 'idle';
    this.transitionProgress = 1.0;
    this.transitionSpeed = 4.5; // Tốc độ chuyển đổi trạng thái mượt mà
    this.time = 0;
    this.actionElapsed = 0; // Thời gian đã trôi qua kể từ khi kích hoạt hành động hiện tại
    this.walkSpeed = 5.5;

    // Bộ nhớ góc xoay mục tiêu để lerp mượt mà
    this.currentPose = this.createEmptyPose();
    this.targetPose = this.createEmptyPose();
  }

  createEmptyPose() {
    return {
      rootY: 0,
      rootZ: 0,
      pelvisRotX: 0,
      pelvisRotY: 0,
      pelvisRotZ: 0,
      torsoRotX: 0,
      torsoRotY: 0,
      torsoRotZ: 0,
      headRotX: 0,
      headRotY: 0,
      headRotZ: 0,
      leftArmRotX: 0,
      leftArmRotY: 0,
      leftArmRotZ: 0,
      rightArmRotX: 0,
      rightArmRotY: 0,
      rightArmRotZ: 0,
      leftForearmRotX: 0,
      leftForearmRotY: 0,
      leftForearmRotZ: 0,
      rightForearmRotX: 0,
      rightForearmRotY: 0,
      rightForearmRotZ: 0,
      leftLegRotX: 0,
      leftLegRotY: 0,
      leftLegRotZ: -0.018,
      rightLegRotX: 0,
      rightLegRotY: 0,
      rightLegRotZ: 0.018,
      leftCalfRotX: 0,
      rightCalfRotX: 0
    };
  }

  play(actionName) {
    if (this.currentAction === actionName && this.targetAction === actionName) {
      return;
    }
    this.actionElapsed = 0;
    this.targetAction = actionName;
    this.transitionProgress = 0.0;
  }

  update(delta) {
    this.time += delta;
    this.actionElapsed += delta;

    if (this.transitionProgress < 1.0) {
      this.transitionProgress = Math.min(1.0, this.transitionProgress + delta * this.transitionSpeed);
      if (this.transitionProgress >= 1.0) {
        this.currentAction = this.targetAction;
      }
    }

    // Tính toán tư thế theo trạng thái hiện tại và trạng thái mục tiêu
    const poseFrom = this.computePoseForAction(this.currentAction, this.time, this.actionElapsed);
    const poseTo = this.computePoseForAction(this.targetAction, this.time, this.actionElapsed);

    // Lerp giữa poseFrom và poseTo
    const t = this.easeInOutQuad(this.transitionProgress);
    const interpolated = this.lerpPose(poseFrom, poseTo, t);

    // Áp dụng lên mesh Three.js
    this.applyPoseToMesh(interpolated);
  }

  easeInOutQuad(x) {
    return easeInOutQuad(x);
  }

  lerp(a, b, t) {
    return lerp(a, b, t);
  }

  lerpPose(from, to, t) {
    return lerpPose(from, to, t);
  }

  computePoseForAction(action, time, elapsed) {
    const pose = this.createEmptyPose();
    const handler = ACTION_POSES[action];
    if (handler) {
      handler(pose, time, elapsed, this);
    }
    return pose;
  }

  applyPoseToMesh(pose) {
    const {
      pelvis,
      torso,
      head,
      leftArm,
      rightArm,
      leftLeg,
      rightLeg,
      leftForearm,
      rightForearm,
      leftCalf,
      rightCalf
    } = this.parts;

    // pelvis độ cao và góc xoay toàn thân (rất quan trọng cho tư thế ngã/lăn)
    if (pelvis) {
      pelvis.position.y = this.basePelvisY + (pose.rootY || 0);
      pelvis.position.z = pose.rootZ || 0;
      pelvis.rotation.x = pose.pelvisRotX || 0;
      pelvis.rotation.y = pose.pelvisRotY || 0;
      pelvis.rotation.z = pose.pelvisRotZ || 0;
    }

    const elderTorsoOffset = this.parts.elderPosture ? 0.12 : 0;
    const elderHeadOffset = this.parts.elderPosture ? -0.08 : 0;

    if (torso) {
      torso.rotation.x = (pose.torsoRotX || 0) + elderTorsoOffset;
      torso.rotation.y = pose.torsoRotY || 0;
      torso.rotation.z = pose.torsoRotZ || 0;
    }

    if (head) {
      head.rotation.x = (pose.headRotX || 0) + elderHeadOffset;
      head.rotation.y = pose.headRotY || 0;
      head.rotation.z = pose.headRotZ || 0;
    }

    if (leftArm) {
      leftArm.rotation.x = pose.leftArmRotX || 0;
      leftArm.rotation.y = pose.leftArmRotY || 0;
      leftArm.rotation.z = pose.leftArmRotZ || 0;
    }

    if (rightArm) {
      rightArm.rotation.x = pose.rightArmRotX || 0;
      rightArm.rotation.y = pose.rightArmRotY || 0;
      rightArm.rotation.z = pose.rightArmRotZ || 0;
    }

    if (leftForearm) {
      leftForearm.rotation.x = pose.leftForearmRotX || 0;
      leftForearm.rotation.y = pose.leftForearmRotY || 0;
      leftForearm.rotation.z = pose.leftForearmRotZ || 0;
    }

    if (rightForearm) {
      rightForearm.rotation.x = pose.rightForearmRotX || 0;
      rightForearm.rotation.y = pose.rightForearmRotY || 0;
      rightForearm.rotation.z = pose.rightForearmRotZ || 0;
    }

    if (leftLeg) {
      leftLeg.rotation.x = pose.leftLegRotX || 0;
      leftLeg.rotation.y = pose.leftLegRotY || 0;
      leftLeg.rotation.z = pose.leftLegRotZ !== undefined ? pose.leftLegRotZ : -0.018;
    }

    if (rightLeg) {
      rightLeg.rotation.x = pose.rightLegRotX || 0;
      rightLeg.rotation.y = pose.rightLegRotY || 0;
      rightLeg.rotation.z = pose.rightLegRotZ !== undefined ? pose.rightLegRotZ : 0.018;
    }

    if (leftCalf) {
      leftCalf.rotation.x = pose.leftCalfRotX || 0;
    }

    if (rightCalf) {
      rightCalf.rotation.x = pose.rightCalfRotX || 0;
    }
  }
}
